import React, {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {Button, FlexToast, useExternalControls} from "laravel-inertia-backend";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import {formatDateDE, formatDurationWords, formatElapsedClock, friendlyErrorMessage, hasAbility} from "./format.js";

const POLL_INTERVAL_MS = 5000;

const RECALCULATE_HELP =
    "Baut die vorberechneten Auswertungstabellen aus allen Rechnungs- und Gutschriftpositionen neu auf. " +
    "Dauert rund 4–5 Minuten (seit der Aufschlüsselung nach Farbton) und läuft im Hintergrund; bis dahin zeigen alle Seiten den bisherigen Stand. " +
    "Nötig nur nach Änderungen an Zuordnungen oder Ausschlüssen oder nach einem Neuimport – sonst geschieht " +
    "es automatisch jede Nacht um 04:50 Uhr.";

/**
 * Datenstand-Hinweis samt Knopf "Neu berechnen": stößt den Vorberechnungs-Job
 * an (POST /vertrieb/neu-berechnen) und pollt danach GET /vertrieb/daten/status
 * alle 5 Sekunden, bis er fertig ist - kein Seiten-Reload, Rückmeldung per
 * Toast. Erkennt beim Laden der Seite auch einen bereits laufenden Auftrag
 * (z. B. den nächtlichen Lauf um 04:50 Uhr oder von einem anderen Nutzer
 * gestartet) und setzt die Anzeige fort, ohne dafür einen eigenen Toast zu
 * zeigen. `onRecalculated` liefert den neuen `aggregated_at`-Zeitstempel
 * zurück, damit die aufrufende Seite ihn als zusätzlichen Parameter mitgeben
 * und so ein Nachladen der Tabellen auslösen kann.
 */
export default function DataStatusBar({meta, onRecalculated}) {
    const {addToast} = useExternalControls();
    const [status, setStatus] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const pollRef = useRef(null);
    const tickRef = useRef(null);
    const cancelledRef = useRef(false);
    const knownFinishedAtRef = useRef(undefined);
    const knownErrorRef = useRef(undefined);

    const stopTick = () => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    };

    const startTick = (sinceIso) => {
        stopTick();
        const since = sinceIso ? new Date(sinceIso).getTime() : Date.now();
        const update = () => setElapsed(Math.max(0, (Date.now() - since) / 1000));
        update();
        tickRef.current = setInterval(update, 1000);
    };

    const fetchStatus = useCallback(
        (initial = false) => {
            axios
                .get("/vertrieb/daten/status")
                .then(({data}) => {
                    if (cancelledRef.current) {
                        return;
                    }
                    setStatus(data);

                    if (initial) {
                        // Beim ersten Laden der Seite nur den Ist-Zustand
                        // übernehmen, ohne für einen bereits abgeschlossenen
                        // oder fehlgeschlagenen Lauf noch nachträglich einen
                        // Toast zu zeigen.
                        knownFinishedAtRef.current = data.finished_at || null;
                        knownErrorRef.current = data.last_error || null;
                    }

                    if (data.running && !data.stale) {
                        startTick(data.started_at || data.queued_at);
                        pollRef.current = setTimeout(() => fetchStatus(false), POLL_INTERVAL_MS);
                        return;
                    }

                    stopTick();

                    if (!initial && data.finished_at && data.finished_at !== knownFinishedAtRef.current) {
                        knownFinishedAtRef.current = data.finished_at;
                        onRecalculated?.(data.aggregated_at);
                        const dauer = formatDurationWords(data.last_duration_s);
                        addToast(
                            <FlexToast
                                variant="success"
                                title="Vertriebsauswertung"
                                autohide
                                message={dauer ? `Die Vorberechnung ist abgeschlossen (${dauer}).` : "Die Vorberechnung ist abgeschlossen."}
                            />
                        );
                    }

                    if (!initial && data.last_error && data.last_error !== knownErrorRef.current) {
                        knownErrorRef.current = data.last_error;
                        addToast(<FlexToast variant="danger" title="Vertriebsauswertung" autohide={false} message={data.last_error} />);
                    }
                })
                .catch(() => {
                    if (cancelledRef.current) {
                        return;
                    }
                    stopTick();
                });
        },
        [addToast, onRecalculated]
    );

    useEffect(() => {
        cancelledRef.current = false;
        fetchStatus(true);
        return () => {
            cancelledRef.current = true;
            if (pollRef.current) {
                clearTimeout(pollRef.current);
            }
            stopTick();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startRecalculation = () => {
        axios
            .post("/vertrieb/neu-berechnen")
            .then(() => {
                fetchStatus(false);
            })
            .catch((e) => {
                addToast(
                    <FlexToast
                        variant="danger"
                        title="Vertriebsauswertung"
                        autohide={e.response?.status === 403}
                        message={friendlyErrorMessage(e, "Die Neuberechnung konnte nicht gestartet werden.")}
                    />
                );
            });
    };

    const isRunning = Boolean(status?.running);
    const buttonDisabled = isRunning && !status?.stale;
    const canRecalculate = hasAbility(meta, "recalculate");

    return (
        <div className="d-flex align-items-center flex-wrap gap-2 small text-muted mb-3">
            <HeaderWithHelp help="Umsatz = Rechnungen minus Gutschriften, ohne Entwürfe und Stornos; ein Set zählt als ein Artikel.">
                nur Rechnungswerte
            </HeaderWithHelp>
            {meta?.stand_label && (
                <span>
                    ·{" "}
                    <HeaderWithHelp help="Zeitpunkt der letzten Vorberechnung: Stichmonat und Jahr, auf dem alle Tabellen dieser Seite beruhen.">
                        {meta.stand_label}
                    </HeaderWithHelp>
                </span>
            )}
            {meta?.data_until && (
                <span>
                    ·{" "}
                    <HeaderWithHelp help="Datum des letzten importierten Rechnungs- oder Gutschriftbelegs.">
                        Daten bis {formatDateDE(meta.data_until)}
                    </HeaderWithHelp>
                </span>
            )}
            {canRecalculate && (
                <div className="ms-auto d-flex flex-column align-items-end gap-1">
                    <OverlayTrigger placement="left" overlay={<Tooltip id="va-recalculate-tooltip">{RECALCULATE_HELP}</Tooltip>}>
                        <span>
                            <Button size="sm" variant="outline-secondary" disabled={buttonDisabled} onClick={startRecalculation}>
                                {isRunning ? `Wird berechnet … (${formatElapsedClock(elapsed)})` : "Neu berechnen"}
                            </Button>
                        </span>
                    </OverlayTrigger>
                    {isRunning && status?.waiting_for_worker && (
                        <div className="text-warning" style={{maxWidth: 260}}>
                            Der Hintergrunddienst hat den Auftrag noch nicht übernommen. Auf diesem System läuft
                            möglicherweise kein Hintergrunddienst.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
